import Button from '@payaca/components/button/Button';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import PaginationControl from '@payaca/components/paginationControl/PaginationControl';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import {
  createTemplate,
  getListedTemplatesPage,
} from '@payaca/store/templates/templatesActions';
import { PublicEntityTemplate } from '@payaca/types/entity-templates';
import {
  GetListedTemplatesQueryParams,
  ListedTemplatesPage,
  SortBy,
} from '@payaca/types/listedTemplateTypes';
import { SortDirection } from '@payaca/types/listViewTypes';
import { FC, useCallback, useEffect, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import CreateEditEntityTemplateModal from '../createEditEntityTemplateModal/CreateEditEntityTemplateModal';
import EmptyState from '../emptyState/EmptyState';
import PersistedEntityTemplateCard from '../entityTemplateCard/PersistedEntityTemplateCard';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import SearchField from '../searchField/SearchField';
import './EntityTemplatesControl.sass';

type Props = {
  entityType: PublicEntityTemplate<
    'task' | 'scheduledEvent' | 'proposal'
  >['entityType'];
};
const ENTITY_TYPE_LABELS: Record<Props['entityType'], string> = {
  task: 'Task',
  scheduledEvent: 'Event',
  proposal: 'Proposal',
};

const EntityTemplatesControl: FC<Props> = ({
  entityType,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createErrorMessage, setCreateErrorMessage] = useState<string>();

  const [getListedTemplatesRequestData, setGetListedTemplatesRequestData] =
    useState<GetListedTemplatesQueryParams>({
      pageNumber: 1,
      pageSize: 25,
      sortBy: SortBy.NAME,
      sortDirection: SortDirection.ASCENDING,
      entityTypes: [entityType],
    });

  const [listedTemplatesPage, setListedTemplatesPage] = useState<
    ListedTemplatesPage | undefined
  >(undefined);

  const [isGettingPage, setIsGettingPage] = useState(false);

  const handleGetListedTemplatesPage = useCallback(
    (queryParams: GetListedTemplatesQueryParams) => {
      setIsGettingPage(true);

      dispatch(
        getListedTemplatesPage.request({
          queryParams: queryParams,
          callback: (page: ListedTemplatesPage) => {
            setListedTemplatesPage(page);
            setIsGettingPage(false);
          },
          onErrorCallback: () => {
            setIsGettingPage(false);
          },
        })
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    []
  );

  useEffect(() => {
    setListedTemplatesPage(undefined);
    setGetListedTemplatesRequestData({
      pageNumber: 1,
      pageSize: 25,
      sortBy: SortBy.NAME,
      sortDirection: SortDirection.ASCENDING,
      entityTypes: [entityType],
    });
  }, [entityType]);

  useEffect(() => {
    handleGetListedTemplatesPage(getListedTemplatesRequestData);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'handleGetListedTemplatesPage'
  }, [getListedTemplatesRequestData]);

  return (
    <div className="entity-templates-control">
      <div className="search-field-container">
        {entityType === 'task' && (
          <SearchField
            styleVariant={InputStyleVariant.UNSTYLED}
            placeholder="Search templates"
            onSearchTermChange={(searchTerm?: string) =>
              setGetListedTemplatesRequestData((requestData) => ({
                ...requestData,
                pageNumber: 1,
                searchTerm: searchTerm,
              }))
            }
          />
        )}
        {entityType === 'scheduledEvent' && (
          <SearchField
            styleVariant={InputStyleVariant.UNSTYLED}
            placeholder="Search templates"
            onSearchTermChange={(searchTerm?: string) =>
              setGetListedTemplatesRequestData((requestData) => ({
                ...requestData,
                pageNumber: 1,
                searchTerm: searchTerm,
              }))
            }
          />
        )}
        {entityType === 'proposal' && (
          <SearchField
            styleVariant={InputStyleVariant.UNSTYLED}
            placeholder="Search templates"
            onSearchTermChange={(searchTerm?: string) =>
              setGetListedTemplatesRequestData((requestData) => ({
                ...requestData,
                pageNumber: 1,
                searchTerm: searchTerm,
              }))
            }
          />
        )}
        {isGettingPage && <MiniLoader />}
        <PermissionGuard
          renderIfHasPermissions={[TemplatesPermissions.PERSIST_TEMPLATE]}
        >
          <Button
            onClick={() => {
              if (entityType === 'proposal') {
                history.push(`/templates/edit?entityType=${entityType}`);
              } else {
                setShowCreateTemplateModal(true);
              }
            }}
          >
            Create {ENTITY_TYPE_LABELS[entityType]} template
          </Button>
        </PermissionGuard>
      </div>
      {listedTemplatesPage && (
        <div className="listed-templates-container">
          {listedTemplatesPage.items.map((template, i) => {
            return (
              <PersistedEntityTemplateCard
                showEntityTypeIcon={true}
                key={i}
                template={template}
                onUpdateEntityTemplateSuccess={() => null}
                allowModifyTemplateNameDescription={true}
                onDeleteEntityTemplateSuccess={() => {
                  handleGetListedTemplatesPage(getListedTemplatesRequestData);
                }}
              />
            );
          })}
        </div>
      )}
      {!isGettingPage &&
        !listedTemplatesPage?.items?.length &&
        !getListedTemplatesRequestData?.searchTerm?.length && (
          <EmptyState configName={'templates'} arrowIndication={false} />
        )}
      {listedTemplatesPage && (
        <PaginationControl
          pageNumber={listedTemplatesPage.pageNumber}
          pageSize={listedTemplatesPage.pageSize}
          totalItemCount={listedTemplatesPage.totalItemCount}
          onSelectPage={(pageNumber) => {
            setGetListedTemplatesRequestData((requestData) => ({
              ...requestData,
              pageNumber,
            }));
          }}
          allowPageSizeSelection={false}
        />
      )}

      <CreateEditEntityTemplateModal
        isOpen={showCreateTemplateModal}
        onClose={() => {
          setShowCreateTemplateModal(false);
          setCreateErrorMessage(undefined);
        }}
        action="Create"
        template={{
          entityType: entityType,
          data: {},
        }}
        isProcessing={isCreating}
        errorMessage={createErrorMessage}
        onRequestSave={(t) => {
          setIsCreating(true);
          setCreateErrorMessage(undefined);
          dispatch(
            createTemplate.request({
              template: t,
              callback: () => {
                setIsCreating(false);
                setShowCreateTemplateModal(false);
                handleGetListedTemplatesPage(getListedTemplatesRequestData);
              },
              onErrorCallback: () => {
                setIsCreating(false);
                setCreateErrorMessage(
                  'Failed to create template. Please try again.'
                );
              },
            })
          );
        }}
        allowModifyTemplateNameDescription={true}
      />
    </div>
  );
};
export default EntityTemplatesControl;
